import { styled } from "goober";
import { type ReactElement, type ReactNode, useState } from "react";
import { Row, SubtleButton } from "./SearchableDropdown";
import { Dropdown } from "./Dropdown";
import { Icon, type IconName } from "./Icon";
import { Link, isInAppNavigation } from "./Link";
import { Text } from "./Text";

type MenuItem = {
  left?: ReactNode;
  label: string;
  onClick: () => void;
};

const ManageRow = styled(Row)`
  & > .manage-row-actions {
    opacity: 0;
    pointer-events: none;
  }

  &:is(:hover, :focus-within) > .manage-row-actions,
  &[data-menu-open="true"] > .manage-row-actions {
    opacity: 1;
    pointer-events: auto;
  }

  @media (hover: none) {
    & > .manage-row-actions {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

const rowContentStyles = `
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  text-decoration: none;
  color: inherit;
`;

const RowContent = styled("div")`
  ${rowContentStyles}
`;

const RowContentLink = styled(Link)`
  ${rowContentStyles}
`;

export function PopoverManageRow({
  icon,
  title,
  subtitle,
  dimmed,
  menuItems,
  to,
  onNavigate,
  menuLabel,
  menuHidden,
}: {
  icon: IconName | ReactElement;
  title: string;
  subtitle?: ReactNode;
  dimmed?: boolean;
  menuItems: MenuItem[];
  to?: string;
  onNavigate?: () => void;
  menuLabel?: string;
  menuHidden?: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const content = (
    <>
      <div style={{ display: "flex", flexShrink: 0 }}>
        {typeof icon === "string" ? <Icon name={icon} size={16} /> : icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text
          ellipsis
          variant="text-regular"
          color={dimmed ? "var(--text-secondary)" : "var(--text-primary)"}
        >
          {title}
        </Text>
        {subtitle && (
          <Text ellipsis variant="text-small" color="var(--text-secondary)">
            {subtitle}
          </Text>
        )}
      </div>
    </>
  );
  return (
    <ManageRow data-menu-open={menuOpen} style={{ cursor: "default" }}>
      {to ? (
        <RowContentLink
          to={to}
          data-nav-row=""
          onMouseDown={(event: React.MouseEvent) => {
            if (isInAppNavigation(event)) onNavigate?.();
          }}
          onClick={(event: React.MouseEvent) => {
            if (isInAppNavigation(event) && event.detail === 0) onNavigate?.();
          }}
        >
          {content}
        </RowContentLink>
      ) : (
        <RowContent>{content}</RowContent>
      )}
      {!menuHidden && (
        <div
          className="manage-row-actions"
          style={{
            flexShrink: 0,
            marginLeft: "auto",
            marginRight: -4,
          }}
        >
          <Dropdown
            align="bottom"
            minWidth={160}
            onOpenChange={setMenuOpen}
            opener={
              <SubtleButton
                variant="secondary"
                icon="more"
                style={{ borderRadius: 9999 }}
                aria-label={menuLabel ?? `Actions for ${title}`}
                data-nav-row={to ? undefined : ""}
              />
            }
            sections={[
              {
                items: menuItems.map((m) => ({
                  type: "button" as const,
                  left: m.left,
                  label: m.label,
                  onClick: m.onClick,
                })),
              },
            ]}
          />
        </div>
      )}
    </ManageRow>
  );
}
